import type { SessionUser, TokenSummary } from '../../shared/types'
import { ApiError, createToken, deleteToken, listTokens } from '../api'
import { buildFrame } from '../frame'
import { saucePicker } from '../saucepicker'
import { copyText, formatRelative, h, plural, toast } from '../ui'

export function tokensView(root: HTMLElement, user: SessionUser): () => void {
  const frame = buildFrame({ user, active: 'tokens' })
  let disposed = false
  let tokens: TokenSummary[] = []

  const nameInput = h('input', {
    class: 'title-input',
    type: 'text',
    placeholder: 'What is this token for? (e.g. laptop)',
    maxlength: '60',
    'aria-label': 'Token name',
  })

  const createButton = h(
    'button',
    { class: 'control primary', type: 'button', onclick: () => void mint() },
    ['Create token'],
  )

  frame.controls.append(nameInput, saucePicker(() => {}), createButton)

  const body = h('div', { class: 'listing' })
  frame.stage.append(body)
  root.append(frame.element)
  document.title = 'tokens · pastabin'
  frame.statusLeft.textContent = 'loading…'

  void load()

  async function load(): Promise<void> {
    try {
      const result = await listTokens()
      if (disposed) return
      tokens = result.tokens
      renderRows()
    } catch (error) {
      if (disposed) return
      body.replaceChildren(
        h('div', { class: 'notice' }, [
          h('h1', {}, ['Could not load your tokens']),
          h('p', {}, [error instanceof ApiError ? error.message : 'Something went wrong.']),
        ]),
      )
      frame.statusLeft.textContent = 'error'
    }
  }

  async function mint(): Promise<void> {
    const name = nameInput.value.trim()
    if (!name) {
      toast('Give the token a name first')
      nameInput.focus()
      return
    }
    createButton.disabled = true
    try {
      const result = await createToken(name)
      if (disposed) return
      tokens = [result.token, ...tokens]
      nameInput.value = ''
      renderRows()
      showSecret(result.secret)
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'Could not create that token')
    } finally {
      createButton.disabled = false
    }
  }

  /** The plaintext exists exactly once — make that unmissable. */
  function showSecret(secret: string): void {
    const field = h('input', { readonly: true, spellcheck: 'false', value: secret })
    field.addEventListener('focus', () => field.select())

    const row = h('div', { class: 'linkrow' }, [
      h('b', { style: 'color:var(--danger);white-space:nowrap' }, ['Copy it now:']),
      field,
      h(
        'button',
        {
          class: 'control',
          type: 'button',
          onclick: async () => {
            const copied = await copyText(secret)
            toast(copied ? 'Token copied' : 'Could not copy')
          },
        },
        ['Copy'],
      ),
      h(
        'button',
        {
          class: 'control',
          type: 'button',
          onclick: async () => {
            const copied = await copyText(
              `curl --data-binary @file.txt -H "authorization: bearer ${secret}" ${location.origin}`,
            )
            toast(copied ? 'curl one-liner copied' : 'Could not copy')
          },
        },
        ['Copy curl'],
      ),
      h('button', { class: 'control', type: 'button', onclick: () => row.remove() }, ['Done']),
    ])
    frame.insertBelowBar(row)
    field.focus()
  }

  function renderRows(): void {
    frame.statusLeft.textContent = plural(tokens.length, 'token')

    if (!tokens.length) {
      body.replaceChildren(
        h('div', { class: 'notice' }, [
          h('h1', {}, ['No tokens yet']),
          h('p', {}, [
            'A token lets you upload from a terminal: pipe a file to this host with the token as a bearer header.',
          ]),
        ]),
      )
      return
    }

    body.replaceChildren(
      h('table', { class: 'table' }, [
        h('thead', {}, [
          h('tr', {}, [
            h('th', {}, ['Name']),
            h('th', {}, ['Token']),
            h('th', { class: 'hide-sm' }, ['Created']),
            h('th', {}, ['Last used']),
            h('th', {}, ['']),
          ]),
        ]),
        h(
          'tbody',
          {},
          tokens.map((token) => {
            const tr = h('tr', {}, [
              h('td', {}, [h('span', { class: 'row-title' }, [token.name])]),
              h('td', { class: 'muted mono' }, [token.preview]),
              h('td', { class: 'hide-sm muted', title: new Date(token.createdAt).toLocaleString() }, [
                formatRelative(token.createdAt),
              ]),
              h('td', { class: 'muted' }, [
                token.lastUsedAt === null ? 'never' : formatRelative(token.lastUsedAt),
              ]),
              h('td', { class: 'row-actions' }, [
                h(
                  'button',
                  { class: 'control', type: 'button', onclick: () => void revoke(token, tr) },
                  ['Revoke'],
                ),
              ]),
            ])
            return tr
          }),
        ),
      ]),
    )
  }

  async function revoke(token: TokenSummary, tr: HTMLElement): Promise<void> {
    if (!confirm(`Revoke “${token.name}”? Anything using it stops working immediately.`)) return
    try {
      await deleteToken(token.id)
      if (disposed) return
      tokens = tokens.filter((candidate) => candidate.id !== token.id)
      tr.remove()
      renderRows()
      toast('Revoked')
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'Could not revoke that token')
    }
  }

  return () => {
    disposed = true
  }
}
